/**
 * Product Gallery Navigation Enhancement
 * Adds clickable left/right navigation areas to the product gallery
 * macOS Safari-optimized with instant scrolling (no animation) for best performance
 * iOS Safari works fine with smooth scroll, so it's excluded
 */

class ProductGalleryNavigation {
	constructor() {
		this.navigationCooldown = new WeakMap()
		this.scrollAnimationFrames = new WeakMap()
		const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
		this.isMacOSSafari = isSafari && !isIOS
		this.safariInstantScroll = true
		this.isPointerFine = window.matchMedia && window.matchMedia('(pointer: fine)').matches
		this.init()
	}

	init() {
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => this.setupNavigation())
		} else {
			this.setupNavigation()
		}
	}

	setupNavigation() {
		const galleries = document.querySelectorAll('product-gallery')
		
		galleries.forEach(gallery => {
			const prevButton = gallery.querySelector('.product-gallery__navigation-area--prev')
			const nextButton = gallery.querySelector('.product-gallery__navigation-area--next')
			const carousel = gallery.querySelector('.product-gallery__carousel')
			
			if (!carousel) {
				return
			}

			this.navigationCooldown.set(carousel, false)
			this.scrollAnimationFrames.set(carousel, null)

			if (this.isPointerFine && prevButton && nextButton) {
				prevButton.addEventListener('click', (e) => {
					e.preventDefault()
					e.stopPropagation()
					this.navigateCarousel(carousel, 'previous')
				})

				nextButton.addEventListener('click', (e) => {
					e.preventDefault()
					e.stopPropagation()
					this.navigateCarousel(carousel, 'next')
				})
			}


			this.updateNavigationVisibility(gallery)
			
			carousel.addEventListener('carousel:change', () => {
				this.updateNavigationVisibility(gallery)
			})
		})
	}

	navigateCarousel(carousel, direction) {
		if (this.navigationCooldown.get(carousel)) {
			return
		}

		if (this.scrollAnimationFrames.get(carousel)) {
			cancelAnimationFrame(this.scrollAnimationFrames.get(carousel))
			this.scrollAnimationFrames.set(carousel, null)
		}

		if (typeof carousel.select === 'function') {
			const currentIndex = carousel.selectedIndex || 0
			const mediaCount = carousel.querySelectorAll('.product-gallery__media:not([hidden])').length
			
			let targetIndex
			
			if (direction === 'previous') {
				targetIndex = currentIndex === 0 ? mediaCount - 1 : currentIndex - 1
			} else if (direction === 'next') {
				targetIndex = currentIndex >= mediaCount - 1 ? 0 : currentIndex + 1
			}
			
			if (targetIndex !== undefined) {
				this.navigationCooldown.set(carousel, true)
				
				const frame = requestAnimationFrame(() => {
					carousel.select(targetIndex)
					this.scrollAnimationFrames.set(carousel, null)
				})
				
				this.scrollAnimationFrames.set(carousel, frame)
				
				const cooldownTime = this.isMacOSSafari && this.safariInstantScroll ? 100 : (this.isMacOSSafari ? 400 : 300)
				setTimeout(() => {
					this.navigationCooldown.set(carousel, false)
				}, cooldownTime)
			}
		} else if (typeof carousel.previous === 'function' && typeof carousel.next === 'function') {
			this.navigationCooldown.set(carousel, true)
			
			const frame = requestAnimationFrame(() => {
				if (direction === 'previous') {
					carousel.previous()
				} else if (direction === 'next') {
					carousel.next()
				}
				this.scrollAnimationFrames.set(carousel, null)
			})
			
			this.scrollAnimationFrames.set(carousel, frame)
			
			const cooldownTime = this.isMacOSSafari && this.safariInstantScroll ? 100 : (this.isMacOSSafari ? 400 : 300)
			setTimeout(() => {
				this.navigationCooldown.set(carousel, false)
			}, cooldownTime)
		}
	}

	updateNavigationVisibility(gallery) {
		const carousel = gallery.querySelector('.product-gallery__carousel')
		const prevButton = gallery.querySelector('.product-gallery__navigation-area--prev')
		const nextButton = gallery.querySelector('.product-gallery__navigation-area--next')
		
		if (!carousel || !prevButton || !nextButton) {
			return
		}

		// Hide navigation if there's only one image or no images
		const mediaCount = carousel.querySelectorAll('.product-gallery__media:not([hidden])').length
		const overlay = gallery.querySelector('.product-gallery__navigation-overlay')
		
		if (mediaCount <= 1) {
			overlay?.style.setProperty('display', 'none')
		} else {
			overlay?.style.removeProperty('display')
			
			// With looping enabled, buttons are always enabled and fully visible
			prevButton.style.opacity = '1'
			nextButton.style.opacity = '1'
			prevButton.disabled = false
			nextButton.disabled = false
		}
	}

}

// Initialize the navigation enhancement
new ProductGalleryNavigation()